import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewPriorty from '@app/src/components/Drawer/Views/DrawerViewPriorty'
import CheckboxCell from '@app/src/components/Table/Cells/CheckboxCell'
import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import EnrichedValueCell from '@app/src/components/Table/Cells/EnrichedValueCell'
import PriorityCell from '@app/src/components/Table/Cells/PriorityCell'
import SimpleRiskChipCell from '@app/src/components/Table/Cells/SimpleRiskChipCell'
import { useProviderEnrichedValues } from '@app/src/hooks/useProviderEnrichedValues'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { Provider, RiskTableView } from '@app/src/types/organizations'
import type { Risk, RiskType } from '@app/src/types/resourceExplorer'
import { resolveSubIndustry } from '@app/src/utils/providerEnrichmentUtils'
import { TableRow } from '@mui/material'
import React, { useState } from 'react'
import type { RiskScreeningInfoDialogProps } from './RiskScreeningInfoDialog'
import RiskScreeningInfoDialog from './RiskScreeningInfoDialog'

interface RiskScreeningIndustryRowProps {
  row: RiskTableView
  enabledRiskTypes: RiskType[]
  activeFilters: FilterGroup[]
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>, provider: Provider) => void
  selectedProviders: Array<number>
}

const getIndustryRisk = (row: RiskTableView, riskType: RiskType): Risk | undefined => {
  return row.risks.find(r => r.riskTypeId === riskType.id)?.industry
}

const RiskScreeningIndustryRow: React.FC<RiskScreeningIndustryRowProps> = ({
  row,
  enabledRiskTypes,
  onCheckboxChange,
  selectedProviders,
  activeFilters,
}) => {
  const { openDrawer } = useDrawer()
  const [riskDialogContent, setRiskDialogContent] = useState<RiskScreeningInfoDialogProps['riskDialogContent']>()

  const riskFilters = activeFilters.find(f => f.name === 'riskStatus')?.filters.flatMap(f => f.value)
  const isChecked = Boolean(selectedProviders?.some(selectedProviderId => selectedProviderId === row.parentObject.id))
  const subIndustry = resolveSubIndustry(row.parentObject)
  const enrichedValues = useProviderEnrichedValues(row.parentObject)

  return (
    <>
      <TableRow>
        <CheckboxCell checked={isChecked} onChange={e => onCheckboxChange(e, row.parentObject)} />
        <CompanyCell company={row.parentObject} drawer />
        <EnrichedValueCell
          value={enrichedValues.subIndustry.value}
          isEnriched={enrichedValues.subIndustry.isEnriched}
        />
        {enabledRiskTypes.map((riskType, index) => {
          const risk = getIndustryRisk(row, riskType)

          return (
            <SimpleRiskChipCell
              key={index}
              riskStatus={risk?.status}
              disabled={!riskFilters?.includes(risk?.status)}
              onClick={() => {
                setRiskDialogContent({
                  providerName: row.parentObject.name,
                  countryName: subIndustry?.description,
                  risk,
                  industry: subIndustry?.description,
                  industryCode: subIndustry?.code,
                })
              }}
            />
          )
        })}
        <PriorityCell
          value={row.parentObject.priority}
          onClick={() => openDrawer(<DrawerViewPriorty provider={row.parentObject} />)}
          size="small"
        />
      </TableRow>

      <RiskScreeningInfoDialog
        riskDialogContent={riskDialogContent}
        setRiskDialogContent={setRiskDialogContent}
        isIndustry
      />
    </>
  )
}

export default RiskScreeningIndustryRow
